import { useEffect, useRef } from 'react';

export default function ScienceSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="technology"
      className="relative w-full bg-background-50 py-20 md:py-32 px-6 md:px-12 lg:px-20"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <div className="reveal opacity-0 mb-4">
            <span className="font-body text-xs font-semibold tracking-widest text-accent-500 uppercase">
              Science & Research
            </span>
          </div>
          <h2 className="reveal opacity-0 font-display text-3xl md:text-4xl lg:text-5xl font-medium text-foreground-50 leading-tight mb-4">
            Our A.I. Technology: Beyond Words
          </h2>
          <p className="reveal opacity-0 font-body text-base text-foreground-400 max-w-3xl mx-auto">
            At VOXEA, we have developed an Artificial Intelligence Foundation Model utilizing human voice-based acoustic modeling, which analyzes how something is said rather than just what is said.
          </p>
        </div>

        {/* VOXEA Advantage cards */}
        <div className="reveal opacity-0 grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {[
            {
              icon: 'ri-brain-line',
              title: 'Clinical-Grade A.I. Foundation Model',
              desc: 'Our system is powered by an architecture with more than 1.8 billion parameters, trained on over 102,000 clinical sessions.',
              stat: '1.8B+',
              statLabel: 'Parameters',
            },
            {
              icon: 'ri-global-line',
              title: 'Language-Independent',
              desc: 'Because we focus on physical voice mechanics (acoustics) rather than linguistic content (NLP), our models are universally applicable across different languages and cultures.',
              stat: 'Universal',
              statLabel: 'Language Support',
            },
            {
              icon: 'ri-bar-chart-box-line',
              title: 'High Accuracy',
              desc: 'Our multi-disease detection systems achieve a 91.61% average classification accuracy.',
              stat: '91.61%',
              statLabel: 'Avg Accuracy',
            },
            {
              icon: 'ri-smartphone-line',
              title: 'Non-Invasive & Accessible',
              desc: 'Analysis is performed using everyday devices like smartphones or smart speakers, eliminating the need for expensive hardware or uncomfortable procedures.',
              stat: '45s',
              statLabel: 'Min Sample',
            },
          ].map((item, i) => (
            <div
              key={i}
              className="p-6 md:p-8 rounded-2xl bg-background-100 border border-background-200/70 hover:border-primary-500/30 transition-all duration-500 ease-silk group"
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-primary-500/10 shrink-0">
                  <i className={`${item.icon} text-primary-500 text-xl`} />
                </div>
                <div className="flex-1">
                  <h3 className="font-body text-base font-semibold text-foreground-50 mb-2">
                    {item.title}
                  </h3>
                  <p className="font-body text-sm text-foreground-400 leading-relaxed mb-4">
                    {item.desc}
                  </p>
                  <div className="flex items-baseline gap-2">
                    <span className="font-display text-2xl font-medium text-primary-500">
                      {item.stat}
                    </span>
                    <span className="font-body text-xs text-foreground-600">
                      {item.statLabel}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Clinical Evidence */}
        <div className="reveal opacity-0 bg-background-100 rounded-2xl border border-background-200/70 p-6 md:p-8">
          <h3 className="font-body text-lg font-semibold text-foreground-50 mb-6">
            Clinical Evidence for Disease Detection
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-accent-500 mt-2 shrink-0" />
                <p className="font-body text-sm text-foreground-400 leading-relaxed">
                  <span className="text-foreground-50 font-medium">Neurodegenerative Disorders:</span> Studies in the Framingham Heart Study demonstrate that acoustic features like jitter can predict incident dementia in asymptomatic participants with high accuracy.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-accent-500 mt-2 shrink-0" />
                <p className="font-body text-sm text-foreground-400 leading-relaxed">
                  <span className="text-foreground-50 font-medium">Parkinson's Disease:</span> Research shows that objective measures detect voice changes in up to 78% of early-stage patients.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-accent-500 mt-2 shrink-0" />
                <p className="font-body text-sm text-foreground-400 leading-relaxed">
                  <span className="text-foreground-50 font-medium">Respiratory Health:</span> Voice biomarkers are now being used to predict Type 2 Diabetes and monitor respiratory conditions like asthma.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-accent-500 mt-2 shrink-0" />
                <p className="font-body text-sm text-foreground-400 leading-relaxed">
                  <span className="text-foreground-50 font-medium">Vocal Fold Pathology:</span> A 2025 study highlights HNR and its variability as promising markers for the early detection of laryngeal conditions.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-accent-500 mt-2 shrink-0" />
                <p className="font-body text-sm text-foreground-400 leading-relaxed">
                  <span className="text-foreground-50 font-medium">5-10x Information Density:</span> Acoustic analysis extracts hundreds of dimensions including F0, formants, and MFCCs, capturing the "how" of speech.
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-accent-500 mt-2 shrink-0" />
                <p className="font-body text-sm text-foreground-400 leading-relaxed">
                  <span className="text-foreground-50 font-medium">Superior Accuracy:</span> Modern temporal convolution neural networks using acoustic data have achieved classification accuracies as high as 91.6% across multiple diseases.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Publication */}
        <div className="reveal opacity-0 mt-8 p-6 md:p-8 rounded-2xl bg-primary-500/5 border border-primary-500/20">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-8">
            <div className="flex-1">
              <span className="font-body text-xs font-semibold tracking-wider text-primary-500 uppercase mb-2 block">
                Published Research
              </span>
              <h4 className="font-body text-base font-semibold text-foreground-50 mb-2">
                Clinical study: A deep learning-based model for detecting depression in senior population
              </h4>
              <p className="font-body text-sm text-foreground-400">
                Frontiers in Psychiatry — November 2022
              </p>
            </div>
            <a
              href="https://www.frontiersin.org"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-foreground-50 font-body text-sm font-medium rounded-lg transition-all duration-500 ease-silk group whitespace-nowrap"
            >
              <span>Read Publication</span>
              <i className="ri-external-link-line w-4 h-4 flex items-center justify-center transition-transform duration-300 group-hover:translate-x-0.5" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}